import i18n from 'i18next';
import { OWNED_BY } from '../../../../../common/base-search-result/base-search-result.constants';
import { helpCenter } from '../../../../../constants/contact-data';
import Routes from '../../../../../constants/routes';
import { world } from '../../../../../languages';
import { AVAILABLE_WORLDS, WORLD_GROUPS } from '../../../../../languages/constants';
import isMainWorld from '../../../../../utils/is-main-world';
import isSupplementaryWorld from '../../../../../utils/is-supplementary-world';
import PersonalizationUtil from '../../../../../utils/personalization';
import formatCentsPrice from '../../../../../utils/price/format-cents-price';
import schoolAccount from '../../../../../utils/school-account';
import makeUrl from '../../../../../utils/url/make-url';

export type UserMenuBlock = {
    items: {
        title: string;
        className?: string;
        hide?: boolean;
        link?: string;
        modal?: string;
        textProps?: object;
        toBeHiddenInWorlds?: string[];
    }[];
    title: string;
};

const authorizedMenuLinks = {
    cabinet: (user: User): UserMenuBlock => ({
        items: [
            {
                link: Routes.CABINET_SALES.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.cabinetSales')
            },
            {
                link: Routes.VOUCHERS.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.vouchers')
            },
            {
                link: Routes.MY_SALES.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.mySales')
            },
            {
                link: Routes.CHARTS.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.charts')
            },
            {
                link: Routes.CREDIT_NOTES.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.creditNotes')
            },
            {
                className: user.details.needsSellerInfo ? 'unpaid-invoices' : '',
                link: Routes.CABINET_BANK_DETAILS.path,
                textProps: { 'data-amount': user.details.needsSellerInfo ? '!' : '' },
                title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.cabinetBankDetails')
            },
            {
                hide: !user.isSeller || !isMainWorld(),
                link: Routes.FONTS.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.community.fonts')
            },
            {
                className: '',
                hide: !user.isSeller || isSupplementaryWorld(),
                link:
                    world === AVAILABLE_WORLDS.DE
                        ? 'https://eduki.com/de/autor/2422846/eduki-learning'
                        : 'https://learning.eduki.com',
                title: 'eduki Learning',
                toBeHiddenInWorlds: ['es', 'fr', 'it', 'gr']
            }
        ],
        title: i18n.t('header.userMenu.authorizedMenuLinks.cabinet.header')
    }),
    community: (params: { id: number; isSeller: boolean; slug: string }): UserMenuBlock => ({
        items: [
            // {
            //     link: `${Routes.AUTHOR_MAIN.prefix}/${params.id}/${params.slug}`,
            //     title: i18n.t('header.userMenu.authorizedMenuLinks.community.mypage'),
            //     hide: !params.isSeller
            // },
            {
                link: Routes.CREATE_BLOGPOST.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.community.createBlogpost')
            },
            {
                hide: !params.isSeller,
                link: helpCenter[world],
                title: i18n.t('header.userMenu.authorizedMenuLinks.community.faq'),
                toBeHiddenInWorlds: ['es', 'fr']
            },
            {
                hide: !params.isSeller || !isMainWorld(),
                link: Routes.FONTS.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.community.fonts')
                // toBeHiddenInWorlds: ['it', 'gr']
            },
            {
                className: '',
                hide: !params.isSeller || isSupplementaryWorld(),
                link:
                    world === AVAILABLE_WORLDS.DE
                        ? 'https://eduki.com/de/autor/2422846/eduki-learning'
                        : 'https://learning.eduki.com',
                title: 'eduki Learning',
                toBeHiddenInWorlds: ['es', 'fr', 'it', 'gr']
            }
        ],
        title: i18n.t('header.userMenu.authorizedMenuLinks.community.header')
    }),
    materials: (user = {} as User, currency = {} as Currency): UserMenuBlock => ({
        items: [
            {
                link:
                    schoolAccount.isAdmin(user) && (world === AVAILABLE_WORLDS.DE || world === AVAILABLE_WORLDS.ES)
                        ? `${Routes.BOUGHT_MATERIALS.path}?ob=${OWNED_BY.SCHOOL}`
                        : Routes.BOUGHT_MATERIALS.path,
                title:
                    schoolAccount.isAdmin(user) && (world === AVAILABLE_WORLDS.DE || world === AVAILABLE_WORLDS.ES)
                        ? i18n.t('header.userMenu.authorizedMenuLinks.settings.mySchool')
                        : i18n.t('header.userMenu.authorizedMenuLinks.materials.boughtMaterials')
            },
            {
                link: Routes.AI_TOOLS.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.materials.aiTools'),
                toBeHiddenInWorlds: [...WORLD_GROUPS.SUPPLEMENTARY]
            },
            {
                link: Routes.NEW_INTERACTIVE_MY_MATERIALS.path,
                title: i18n.t('header.interactive.myInteractives'),
                toBeHiddenInWorlds: [...WORLD_GROUPS.SUPPLEMENTARY]
            },
            {
                hide: schoolAccount.isAdmin(user) && world === AVAILABLE_WORLDS.DE,
                link: Routes.FAVORITES.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.materials.favorites')
            },
            {
                hide: !schoolAccount.isAdmin(user) || world !== AVAILABLE_WORLDS.DE,
                link: Routes.SCHOOL_WISHLIST.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.schoolWishlist')
            },
            {
                hide: schoolAccount.isAdmin(user) || (world !== AVAILABLE_WORLDS.DE && world !== AVAILABLE_WORLDS.ES),
                // @ts-ignore
                link: schoolAccount.isVerifiedUser(user)
                    ? `${Routes.BOUGHT_MATERIALS.path}?ob=${OWNED_BY.SCHOOL}`
                    : Routes.MY_SCHOOL.path,
                title: i18n.t(
                    `header.userMenu.authorizedMenuLinks.settings.${
                        schoolAccount.isVerifiedUser(user) ? 'mySchool' : 'noMySchool'
                    }`
                )
            },
            {
                hide: !PersonalizationUtil.showReco(),
                link: Routes.SEASONAL_MATERIALS_LANDING.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.materials.seasonalMaterialsLanding')
            },
            {
                hide:
                    !schoolAccount.hasSchool(user) ||
                    !schoolAccount.isVerifiedUser(user) ||
                    schoolAccount.isAdmin(user) ||
                    world !== AVAILABLE_WORLDS.DE,
                link: Routes.SCHOOL_WISHLIST.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.schoolWishlist')
            },
            {
                className: `${!user.details.unpaidInvoiceOrdersCount ? 'empty' : ''} unpaid-invoices`,
                link: Routes.INVOICES_AND_TAXES.path,
                textProps: { 'data-amount': user.details.unpaidInvoiceOrdersCount },
                title: i18n.t('header.userMenu.authorizedMenuLinks.materials.invoicesAndTaxes')
            },
            {
                className: '',
                hide: !user.hadCredits,
                link: Routes.CREDITS.path,
                title: `${i18n.t('header.userMenu.authorizedMenuLinks.materials.credits', {
                    availableCredits: formatCentsPrice(user.availableCredits)
                })} €`
            }
        ],
        title: i18n.t('header.userMenu.authorizedMenuLinks.materials.header')
    }),
    settings: (user: User): UserMenuBlock => ({
        items: [
            {
                link: makeUrl(user.isSeller ? Routes.AUTHOR_MAIN.path : Routes.USER_PROFILE.path, {
                    id: user.id,
                    slug: user.slug
                }),
                title: i18n.t(
                    `header.userMenu.authorizedMenuLinks.${user.isSeller ? 'settings.authorPage' : 'settings.userPage'}`
                )
            },
            {
                className: '',
                hide: !PersonalizationUtil.showReco(),
                modal: 'PersonalizationModal',
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.yourSubject')
            },
            {
                link: Routes.CABINET_PREFERENCE_CENTER.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.preferenceCenter')
            },
            {
                link: Routes.CABINET_INVOICE_INFO.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.invoiceInfo')
            },
            {
                link: Routes.CABINET_CHANGE_PASSWORD.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.changePassword')
            },
            {
                link: Routes.CABINET_CHANGE_EMAIL.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.changeEmail')
            },
            {
                className: schoolAccount.getData(user)?.waiting_users_count ? 'waiting-users' : '',
                hide: !schoolAccount.isAdmin(user) || (world !== AVAILABLE_WORLDS.DE && world !== AVAILABLE_WORLDS.ES),
                link: Routes.MY_SCHOOL.path,
                textProps: {
                    'data-amount': schoolAccount.getData(user)?.waiting_users_count
                        ? schoolAccount.getData(user)?.waiting_users_count
                        : ''
                },
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.masterSchool')
            },
            {
                link: Routes.CHANGE_COUNTRY.path,
                title: i18n.t('header.userMenu.authorizedMenuLinks.settings.changeCountry')
            }
        ],
        title: i18n.t('header.userMenu.authorizedMenuLinks.settings.header')
    })
};

export enum MENU_STATE_TYPES {
    LOGGED_IN = 'loggedIn',
    LOGIN = 'login',
    REGISTR = 'registr'
}

export default authorizedMenuLinks;
